import React, { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Sparkles, Upload, FileText, Zap, Target, TrendingUp, Edit3, Search, BarChart2, Mail, MessageCircle } from 'lucide-react';
import { useChatContext } from '../providers/ChatProvider';
import { usePreviewContext } from '../providers/PreviewProvider';
import { useAppContext } from '../../../context/AppContext';
import ChatHistory from './ChatHistory';
import ChatInput from './ChatInput';
import TypingIndicator from './TypingIndicator';
import MutationFeedbackToast from './MutationFeedbackToast';

function getStepSuggestions(stepKey) {
  switch (stepKey) {
    case 'summary':
      return ["Write a brief summary", "Suggest summary for developer", "Make summary outcome-focused"];
    case 'experience':
      return ["Draft a Senior Dev role", "Add internship details", "Quantify bullet points"];
    case 'education':
      return ["Add university degree", "Add certification details"];
    case 'projects':
      return ["Add React Web app project", "Add Node.js API project", "Highlight tech stack"];
    case 'skills':
      return ["Suggest JavaScript skills", "Suggest AWS & Cloud skills", "Add communication skills"];
    case 'certificates':
      return ["Add AWS Solutions Architect", "Add Scrum Master cert"];
    case 'review':
      return ["Analyze ATS match score", "Polish writing grammar", "Verify spacing checklist"];
    default:
      return [];
  }
}

// Action chips shown when the user already has an imported resume
const POST_IMPORT_CHIPS = [
  { label: "Improve ATS score",      icon: Target,      prompt: "Analyze my resume's ATS score and tell me the top 3 improvements.", color: '#3b82f6' },
  { label: "Rewrite my experience",  icon: Edit3,        prompt: "Rewrite my work experience bullet points to be more impact-driven with metrics.", color: '#8b5cf6' },
  { label: "Add missing keywords",   icon: Search,       prompt: "What key industry keywords am I missing? Add the most important ones.", color: '#10b981' },
  { label: "Optimize for Google",    icon: Zap,          prompt: "Tailor my resume for a Software Engineer role at Google.", color: '#f59e0b' },
  { label: "Fix my summary",         icon: Edit3,        prompt: "Rewrite my professional summary to be more compelling.", color: '#ec4899' },
  { label: "Full resume analysis",   icon: BarChart2,    prompt: "Give me a detailed analysis of my resume: strengths, weaknesses, and quick wins.", color: '#06b6d4' },
];

// Action chips shown when on a cover letter page with an active cover letter
const COVER_LETTER_CHIPS = [
  { label: "Make it compelling",     icon: Sparkles,     prompt: "Rewrite my cover letter opening to immediately grab the hiring manager's attention.", color: '#a855f7' },
  { label: "Tailor to job",          icon: Target,       prompt: "Help me tailor this cover letter to match a specific job description I'll share.", color: '#3b82f6' },
  { label: "Improve my story",       icon: MessageCircle, prompt: "Help me tell a stronger personal story that connects my background to this role.", color: '#10b981' },
  { label: "Fix tone & grammar",     icon: Edit3,        prompt: "Review my cover letter for tone, clarity, and grammar issues.", color: '#f59e0b' },
  { label: "Make it shorter",        icon: Zap,          prompt: "Tighten my cover letter — make it shorter, punchier, and more impactful.", color: '#ec4899' },
  { label: "Strengths & gaps",       icon: BarChart2,    prompt: "Analyze my cover letter and tell me its strengths and what's missing.", color: '#06b6d4' },
];

// Quick-start role chips for cover letter fresh state
const COVER_LETTER_ROLE_CHIPS = [
  'Software Engineer', 'Product Manager', 'Data Scientist', 'UX Designer', 'Marketing Manager', 'Business Analyst',
];

const RESUME_ROLE_CHIPS = [
  'Full Stack Developer', 'Backend Engineer', 'Data Analyst', 'Product Manager', 'React Developer', 'DevOps Engineer',
];

export default function ChatWorkspace() {
  const {
    reply,
    isStreaming,
    streamingText,
    input,
    setInput,
    handleSend,
    replyEndRef,
    theme,
    mutationFeedback,
  } = useChatContext();

  const {
    resumeData,
    activeCoverLetter,
  } = usePreviewContext();

  const { triggerImportModal, setResumeData, session } = useAppContext();
  const location = useLocation();
  const coachingFiredRef = useRef(false);

  // Detect if we're on a cover letter page
  const isCoverLetterPage = location.pathname.startsWith('/app/cover-letter');

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    replyEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [reply, streamingText, replyEndRef]);

  // Fire the coaching prompt that was embedded in navigation state by CreateMenu.
  // We wait until reply is empty (session fully cleared) to avoid firing into the
  // old session. The ref prevents double-firing on re-renders.
  useEffect(() => {
    const { coachingPrompt, source } = location.state || {};
    if (!coachingPrompt || source !== 'create') return;
    if (coachingFiredRef.current) return;
    if (isStreaming) return;
    // Only fire once reply array is empty (new session loaded, old history cleared)
    if (reply.length > 0) return;
    coachingFiredRef.current = true;
    // Clear stale resume data from previous document so preview resets
    if (setResumeData) setResumeData(null);
    // Brief delay so the chat UI finishes mounting before the message goes out
    const t = setTimeout(() => handleSend(coachingPrompt), 500);
    return () => clearTimeout(t);
  }, [location.state, reply.length, isStreaming, handleSend, setResumeData]);

  // Reset coaching fired ref when navigating to a different document
  const locationKey = location.key;
  useEffect(() => {
    coachingFiredRef.current = false;
  }, [locationKey]);

  // Coordinate welcome typing completion with suggestion chips appearance
  const [welcomeAnimationDone, setWelcomeAnimationDone] = React.useState(() => {
    if (reply.length !== 1 || reply[0]?.role !== 'assistant') return true;
    return !!sessionStorage.getItem('welcome_typed_0');
  });

  useEffect(() => {
    if (reply.length === 1 && reply[0]?.role === 'assistant' && !sessionStorage.getItem('welcome_typed_0')) {
      setWelcomeAnimationDone(false);
      const handleDone = () => {
        setWelcomeAnimationDone(true);
      };
      window.addEventListener('welcome_typing_done', handleDone);
      return () => window.removeEventListener('welcome_typing_done', handleDone);
    } else {
      setWelcomeAnimationDone(true);
    }
  }, [reply]);

  const handleSendPrompt = (promptText) => {
    const msgText = promptText || input;
    if (!msgText?.trim()) return;
    setInput('');
    handleSend(msgText);
  };

  const steps = useMemo(() => {
    const hasInfo = !!(resumeData?.name || resumeData?.email || resumeData?.phone);
    const hasSummary = !!resumeData?.summary;
    const hasExp = !!(resumeData?.experience && resumeData?.experience.length > 0);
    const hasEdu = !!(resumeData?.education && resumeData?.education.length > 0);
    const hasProj = !!(resumeData?.projects && resumeData?.projects.length > 0);
    const hasSkills = !!(resumeData?.skills && resumeData?.skills.length > 0);
    const hasCerts = !!((resumeData?.certifications && resumeData?.certifications.length > 0) || (resumeData?.certificates && resumeData?.certificates.length > 0));

    return [
      { label: 'Basic Info', done: hasInfo, key: 'info' },
      { label: 'Career Goal', done: hasSummary, key: 'summary' },
      { label: 'Work Experience', done: hasExp, key: 'experience' },
      { label: 'Education', done: hasEdu, key: 'education' },
      { label: 'Projects', done: hasProj, key: 'projects' },
      { label: 'Skills', done: hasSkills, key: 'skills' },
      { label: 'Certificates', done: hasCerts, key: 'certificates' },
      { label: 'Review', done: hasInfo && hasSummary && hasExp && hasEdu && hasProj && hasSkills && hasCerts, key: 'review' }
    ];
  }, [resumeData]);

  const completedCount = useMemo(() => steps.filter(s => s.done).length, [steps]);
  const progressPercent = useMemo(() => Math.round((completedCount / steps.length) * 100), [completedCount, steps.length]);

  const currentStep = useMemo(() => {
    return steps.find(s => !s.done) || steps[steps.length - 1];
  }, [steps]);

  const handleChipClick = (suggestionText) => {
    setInput('');
    handleSend(suggestionText);
  };

  const isDark = theme === 'dark';

  return (
    <div className="flex-1 flex flex-col min-h-0 relative">
      {/* Messages area */}
      <div className="flex-1 overflow-y-auto px-4 pt-[72px] md:pt-4 pb-4 relative">
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100%',
          justifyContent: reply.length > 0 ? 'flex-end' : 'center',
          gap: '16px'
        }}>

        {/* ─────────────────────────────────────────────────────────
            EMPTY STATE — context-aware, prominent & welcoming
        ───────────────────────────────────────────────────────── */}
        {reply.length === 0 && !isStreaming && (
          <div
            className="w-full mx-auto animate-fade-in"
            style={{
              maxWidth: 560,
              padding: '8px 0 24px',
              fontFamily: "'Inter', sans-serif",
            }}
          >

            {/* ══════════════════════════════════════════════
                COVER LETTER PAGE
            ══════════════════════════════════════════════ */}
            {isCoverLetterPage ? (

              /* ── Cover letter LOADED state ── */
              activeCoverLetter ? (
                <div>
                  {/* Header */}
                  <div style={{ textAlign: 'center', marginBottom: 28 }}>
                    <div style={{
                      width: 64, height: 64, borderRadius: 20,
                      background: 'linear-gradient(135deg, #7c3aed, #a855f7)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 28, margin: '0 auto 16px',
                      boxShadow: '0 8px 24px rgba(124,58,237,0.4)',
                    }}>✉️</div>
                    <h2 style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 8px', lineHeight: 1.25 }}>
                      {activeCoverLetter.company
                        ? `Cover letter for ${activeCoverLetter.company} ✓`
                        : 'Your cover letter is ready ✓'}
                    </h2>
                    <p style={{ fontSize: 14, color: 'var(--text-secondary)', margin: 0, lineHeight: 1.6 }}>
                      What would you like to improve? Pick an action below or type your own message.
                    </p>
                  </div>

                  {/* Action chips as cards */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
                    {COVER_LETTER_CHIPS.map(chip => {
                      const Icon = chip.icon;
                      return (
                        <button
                          key={chip.label}
                          onClick={() => { setInput(''); handleSend(chip.prompt); }}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 10,
                            padding: '14px 16px',
                            borderRadius: 14,
                            border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)'}`,
                            background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
                            cursor: 'pointer',
                            transition: 'all 0.18s ease',
                            textAlign: 'left',
                          }}
                          onMouseEnter={e => {
                            e.currentTarget.style.background = isDark ? `${chip.color}18` : `${chip.color}12`;
                            e.currentTarget.style.borderColor = `${chip.color}60`;
                            e.currentTarget.style.transform = 'translateY(-1px)';
                          }}
                          onMouseLeave={e => {
                            e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)';
                            e.currentTarget.style.borderColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)';
                            e.currentTarget.style.transform = 'translateY(0)';
                          }}
                        >
                          <div style={{
                            width: 32, height: 32, borderRadius: 10, flexShrink: 0,
                            background: `${chip.color}20`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            <Icon size={15} style={{ color: chip.color }} />
                          </div>
                          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.3 }}>
                            {chip.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

              ) : (
                /* ── Cover letter FRESH state ── */
                <div>
                  {/* Header */}
                  <div style={{ textAlign: 'center', marginBottom: 28 }}>
                    <div style={{
                      width: 64, height: 64, borderRadius: 20,
                      background: 'linear-gradient(135deg, #7c3aed, #a855f7)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 28, margin: '0 auto 16px',
                      boxShadow: '0 8px 24px rgba(124,58,237,0.4)',
                    }}>✉️</div>
                    <div style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '4px 12px', borderRadius: 20, marginBottom: 12,
                      background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.25)',
                    }}>
                      <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e', animation: 'pulse 2s infinite' }} />
                      <span style={{ fontSize: 11, color: '#22c55e', fontWeight: 600 }}>Cover Letter Coach · Online</span>
                    </div>
                    <h2 style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 10px', lineHeight: 1.25 }}>
                      A great cover letter opens doors resumes can't. 🚀
                    </h2>
                    <p style={{ fontSize: 14, color: 'var(--text-secondary)', margin: 0, lineHeight: 1.65 }}>
                      I'll help you craft a compelling, tailored letter that resonates with hiring managers — with the right tone, story, and keywords.
                    </p>
                  </div>

                  {/* What we'll build */}
                  <div style={{
                    padding: '14px 16px', borderRadius: 14, marginBottom: 20,
                    background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)'}`,
                  }}>
                    <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 10px' }}>
                      What we'll craft together
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {[
                        { icon: '✅', text: 'A strong opening hook that grabs attention instantly' },
                        { icon: '✍️', text: 'Your unique story connected to the target role' },
                        { icon: '🎯', text: 'Keywords aligned to the job description' },
                        { icon: '📈', text: 'A confident, professional close that drives action' },
                      ].map(item => (
                        <div key={item.text} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span style={{ fontSize: 14 }}>{item.icon}</span>
                          <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{item.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Role chips */}
                  <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 12px', textAlign: 'center' }}>
                    What role are you applying for? Pick one to start instantly:
                  </p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
                    {COVER_LETTER_ROLE_CHIPS.map(chip => (
                      <button
                        key={chip}
                        onClick={() => handleSend(`Help me write a compelling cover letter for a ${chip} role. Start by asking me about the company and my background.`)}
                        style={{
                          padding: '10px 18px',
                          borderRadius: 50,
                          border: `1px solid ${isDark ? 'rgba(168,85,247,0.3)' : 'rgba(124,58,237,0.2)'}`,
                          background: isDark ? 'rgba(168,85,247,0.08)' : 'rgba(124,58,237,0.05)',
                          color: isDark ? '#c4b5fd' : '#7c3aed',
                          fontSize: 13, fontWeight: 600,
                          cursor: 'pointer', transition: 'all 0.18s ease',
                        }}
                        onMouseEnter={e => {
                          e.currentTarget.style.background = 'rgba(168,85,247,0.18)';
                          e.currentTarget.style.borderColor = 'rgba(168,85,247,0.5)';
                          e.currentTarget.style.transform = 'translateY(-1px)';
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.background = isDark ? 'rgba(168,85,247,0.08)' : 'rgba(124,58,237,0.05)';
                          e.currentTarget.style.borderColor = isDark ? 'rgba(168,85,247,0.3)' : 'rgba(124,58,237,0.2)';
                          e.currentTarget.style.transform = 'translateY(0)';
                        }}
                      >
                        {chip}
                      </button>
                    ))}
                  </div>
                </div>
              )

            ) : (
              /* ══════════════════════════════════════════════
                  RESUME PAGE
              ══════════════════════════════════════════════ */

              /* ── POST-IMPORT STATE: has resume data ── */
              resumeData && (resumeData.name || (resumeData.experience && resumeData.experience.length > 0)) ? (
                <div>
                  {/* Header */}
                  <div style={{ textAlign: 'center', marginBottom: 28 }}>
                    <div style={{
                      width: 64, height: 64, borderRadius: 20,
                      background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      margin: '0 auto 16px',
                      boxShadow: '0 8px 24px rgba(59,130,246,0.4)',
                    }}>
                      <Sparkles size={28} style={{ color: '#fff' }} />
                    </div>
                    <div style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '4px 12px', borderRadius: 20, marginBottom: 12,
                      background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.25)',
                    }}>
                      <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e', animation: 'pulse 2s infinite' }} />
                      <span style={{ fontSize: 11, color: '#22c55e', fontWeight: 600 }}>Resume Coach · Ready to optimize</span>
                    </div>
                    <h2 style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 10px', lineHeight: 1.25 }}>
                      {resumeData.name ? `Welcome back, ${resumeData.name.split(' ')[0]}! 👋` : 'Resume loaded ✓'}
                    </h2>
                    <p style={{ fontSize: 14, color: 'var(--text-secondary)', margin: 0, lineHeight: 1.65 }}>
                      Your resume is ready. What would you like to improve? Choose an action below or just type your question.
                    </p>
                  </div>

                  {/* Action chips as cards */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
                    {POST_IMPORT_CHIPS.map(chip => {
                      const Icon = chip.icon;
                      return (
                        <button
                          key={chip.label}
                          onClick={() => { setInput(''); handleSend(chip.prompt); }}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 10,
                            padding: '14px 16px',
                            borderRadius: 14,
                            border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)'}`,
                            background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
                            cursor: 'pointer',
                            transition: 'all 0.18s ease',
                            textAlign: 'left',
                          }}
                          onMouseEnter={e => {
                            e.currentTarget.style.background = isDark ? `${chip.color}18` : `${chip.color}12`;
                            e.currentTarget.style.borderColor = `${chip.color}60`;
                            e.currentTarget.style.transform = 'translateY(-1px)';
                          }}
                          onMouseLeave={e => {
                            e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)';
                            e.currentTarget.style.borderColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)';
                            e.currentTarget.style.transform = 'translateY(0)';
                          }}
                        >
                          <div style={{
                            width: 32, height: 32, borderRadius: 10, flexShrink: 0,
                            background: `${chip.color}20`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            <Icon size={15} style={{ color: chip.color }} />
                          </div>
                          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.3 }}>
                            {chip.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

              ) : (
                /* ── FRESH STATE: no resume yet ── */
                <div>
                  {/* Top gradient accent bar */}
                  <div style={{
                    height: 4, width: '100%', borderRadius: 4, marginBottom: 28,
                    background: 'linear-gradient(90deg, #3b82f6, #6366f1, #8b5cf6)',
                    boxShadow: '0 0 16px rgba(99,102,241,0.4)',
                  }} />

                  {/* Header */}
                  <div style={{ textAlign: 'center', marginBottom: 28 }}>
                    <div style={{
                      width: 64, height: 64, borderRadius: 20,
                      background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 28, margin: '0 auto 16px',
                      boxShadow: '0 8px 24px rgba(59,130,246,0.4)',
                    }}>🎯</div>
                    <div style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '4px 12px', borderRadius: 20, marginBottom: 14,
                      background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.25)',
                    }}>
                      <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e', animation: 'pulse 2s infinite' }} />
                      <span style={{ fontSize: 11, color: '#22c55e', fontWeight: 600 }}>Resume Coach · AI-Powered · Online</span>
                    </div>
                    <h2 style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 10px', lineHeight: 1.3 }}>
                      Your dream job starts with a resume that gets noticed. 🚀
                    </h2>
                    <p style={{ fontSize: 14, color: 'var(--text-secondary)', margin: 0, lineHeight: 1.7 }}>
                      I'm your personal AI Resume Coach. I'll help you build an ATS-optimized resume
                      that beats 90% of applicants — with real feedback, keyword targeting, and tailored rewrites.
                    </p>
                  </div>

                  {/* What you'll get */}
                  <div style={{
                    padding: '16px', borderRadius: 14, marginBottom: 24,
                    background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)'}`,
                  }}>
                    <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 12px' }}>
                      What we'll build together
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                      {[
                        { icon: '✅', text: 'ATS score analysis' },
                        { icon: '✍️', text: 'Impact-driven bullets' },
                        { icon: '🎯', text: 'Keyword gap analysis' },
                        { icon: '📈', text: 'Section-by-section plan' },
                      ].map(item => (
                        <div key={item.text} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 15 }}>{item.icon}</span>
                          <span style={{ fontSize: 12.5, color: 'var(--text-secondary)', fontWeight: 500 }}>{item.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Role chips */}
                  <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 12px', textAlign: 'center' }}>
                    What role are you targeting? Pick one to get started instantly:
                  </p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', marginBottom: 20 }}>
                    {RESUME_ROLE_CHIPS.map(chip => (
                      <button
                        key={chip}
                        onClick={() => handleSend(`I want to build a resume for a ${chip} role. Please start by asking me about my experience.`)}
                        style={{
                          padding: '10px 18px',
                          borderRadius: 50,
                          border: `1px solid ${isDark ? 'rgba(99,102,241,0.35)' : 'rgba(59,130,246,0.25)'}`,
                          background: isDark ? 'rgba(99,102,241,0.1)' : 'rgba(59,130,246,0.06)',
                          color: isDark ? '#a5b4fc' : '#3b82f6',
                          fontSize: 13, fontWeight: 600,
                          cursor: 'pointer', transition: 'all 0.18s ease',
                        }}
                        onMouseEnter={e => {
                          e.currentTarget.style.background = 'rgba(99,102,241,0.2)';
                          e.currentTarget.style.borderColor = 'rgba(99,102,241,0.5)';
                          e.currentTarget.style.transform = 'translateY(-1px)';
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.background = isDark ? 'rgba(99,102,241,0.1)' : 'rgba(59,130,246,0.06)';
                          e.currentTarget.style.borderColor = isDark ? 'rgba(99,102,241,0.35)' : 'rgba(59,130,246,0.25)';
                          e.currentTarget.style.transform = 'translateY(0)';
                        }}
                      >
                        {chip}
                      </button>
                    ))}
                  </div>

                  {/* Import shortcut */}
                  <button
                    onClick={() => triggerImportModal?.()}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      width: '100%', padding: '13px 16px', borderRadius: 12,
                      background: isDark ? 'rgba(59,130,246,0.07)' : 'rgba(59,130,246,0.05)',
                      border: '1.5px dashed rgba(59,130,246,0.4)',
                      color: '#3b82f6',
                      fontSize: 13, fontWeight: 600,
                      cursor: 'pointer', transition: 'all 0.18s',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.background = 'rgba(59,130,246,0.12)';
                      e.currentTarget.style.borderColor = 'rgba(59,130,246,0.6)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.background = isDark ? 'rgba(59,130,246,0.07)' : 'rgba(59,130,246,0.05)';
                      e.currentTarget.style.borderColor = 'rgba(59,130,246,0.4)';
                    }}
                  >
                    <Upload size={15} />
                    Already have a resume? Import it for an instant AI analysis →
                  </button>
                </div>
              )
            )}
          </div>
        )}

        {/* Message logs */}
        <ChatHistory onSendSuggestion={handleSendPrompt} />

        {/* Mutation feedback toast — slides up after resume mutations */}
        <MutationFeedbackToast items={mutationFeedback || []} theme={theme} />

        {/* Live typing text indicator */}
        <TypingIndicator />

          <div ref={replyEndRef} />
        </div>
      </div>



      {/* Input textbox */}
      <ChatInput onSend={handleSendPrompt} />
    </div>
  );
}
